import { Box, Flex, Text } from '@chakra-ui/react';
import { Modal, Button, useToast, TextArea } from '@morressier/alembic';
import { useEditorialRequestCopyrightForm } from 'src/api/commands';
import { IDecisionModalProps } from '../decisionModals/decisionModalHelper';
import { FormProvider, useForm } from 'react-hook-form';
import { CustomFormFieldComponent } from 'src/components/FormField';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { zodResolver } from '@hookform/resolvers/zod';
import { editorialRequestCopyrightSchema } from '@publishing-engine/domain/validation/index';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons';
import { getFileUploadArrayFFDConfigs } from 'src/utils/formHelpers';

export const EditorialRequestCopyrightFormModal = ({
  onClose,
  submissionId,
  submissionContextId
}: IDecisionModalProps) => {
  const toast = useToast();

  const { refetchSubmission, organizationId } = useSubmissionData();
  const editorialRequestCopyrightMutation = useEditorialRequestCopyrightForm({
    submissionId,
    organizationId,
    submissionContextId,
    invalidateSubmissionQueries: refetchSubmission
  });

  const methods = useForm({
    defaultValues: {
      responseToAuthor: '',
      attachments: []
    },
    resolver: zodResolver(editorialRequestCopyrightSchema)
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    getValues
  } = methods;

  const submit = async () => {
    const formValues = getValues();

    const res = await editorialRequestCopyrightMutation.mutateAsync(formValues);

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: 'Copyright and Publication Agreement Requested',
        description: 'The author will be informed to take action via email.'
      });
      onClose();
    } else {
      toast({
        variant: 'error',
        title: 'Error Requesting Copyright And Publication Agreement'
      });
    }
  };

  return (
    <Modal isOpen onClose={onClose} width="720px">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submit)}>
          <Flex
            padding={8}
            gap={6}
            flexDirection="column"
            maxHeight="80vh"
            overflow="auto"
          >
            <Box>
              <FontAwesomeIcon fontSize={32} icon={faEnvelope} />
            </Box>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingGroup">
                Request Copyright and Publication Agreement
              </Text>
              <Text textStyle="textBody">
                Please provide additional context for the Author about this
                request.
              </Text>
            </Flex>
            <Flex gap={2} flexDirection="column">
              <Flex alignItems="center">
                <Text textStyle="headingItem">Response to Author</Text>
                <Box
                  as="span"
                  color="actionSecondaryCautionContent"
                  textStyle="headingItem"
                >
                  *
                </Box>
              </Flex>
              <TextArea
                value={watch('responseToAuthor')}
                {...register('responseToAuthor')}
                placeholder="Add feedback to the authors"
                isInvalid={Boolean(errors.responseToAuthor)}
                errorMessage={errors?.responseToAuthor?.message}
                rows={3}
              />
            </Flex>
            <Flex gap={2} flexDirection="column">
              <CustomFormFieldComponent
                config={getFileUploadArrayFFDConfigs('attachments')}
                submissionId={submissionId}
                submissionContextId={submissionContextId}
              />
            </Flex>
            <Flex gap={2} justifyContent="flex-end">
              <Button onClick={onClose} variant="secondary">
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={editorialRequestCopyrightMutation.isLoading}
              >
                Request Agreement
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};
